import * as React from "react";
import { useTranslation } from "react-i18next";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";

import BbtiConfigurationForm from "./BbtiConfigurationForm";
import style from "./bms-config.scss";
import { Brand, ComponentFeatures } from "./BmsCommonInterfaces";
import Wizard from "./BmsWizard";
import DownloadServerConfigurationForm from "./DownloadServerConfigurationForm";
import Checkbox from "./form-components/BmsCheckboxComponent";
import GenericConfigurationForm from "./GenericConfigurationForm";
import IdentityVerificationConfigurationForm from "./IdentityVerificationConfigurationForm";
import InsuranceConfigurationForm from "./InsuranceConfigurationForm";
import LeaseConfigurationForm from "./LeaseConfigurationForm";
import NtfConfigurationForm from "./NtfConfigurationForm";
import SdkConfigurationForm from "./SdkConfigurationForm";
import ValidationConfigurationForm from "./ValidationConfigurationForm";
import { Configuration } from "domain/licenses";
import buttons from "styles/buttons.scss";
import form from "styles/form.scss";

import testIds from "testIds.json";

interface Props {
    configuration: Configuration;
    featuresWithAvailableLicenses?: string[];
    uuid?: string;
    created?: string;
    modified?: string;
    edit?: boolean;
    runWizard?: boolean;
    onEdit?: () => void;
    onSuccess: (values: Configuration) => Promise<void>;
    onClose?: () => void;
}

export default function EditBmsConfigurationForm(props: Props): JSX.Element {
    const { t } = useTranslation();
    const sdkTabName = "SDK";
    const [configuration, setConfiguration] = React.useState<Configuration>(
        props.configuration || ({} as Configuration)
    );
    const [showAdvanced, setShowAdvanced] = React.useState(false);

    const formValues = {
        brand: (configuration?.tenantConfiguration?.brand as Brand) || {
            bms_server: "",
            brandId: "",
            image: "",
            appName: "",
            tag_line: "",
            primaryColor: "",
            secondaryColor: "",
            tertiaryColor: "",
            checkContrast: "",
        },
        features: (configuration?.tenantConfiguration?.features as string[]) || [],
        platform: (configuration?.tenantConfiguration?.platform as string) || "app",
        selectedApp: (configuration?.tenantConfiguration?.selectedApp as string) || "bmsApp",
        marketing_name_url: (configuration?.tenantConfiguration?.marketing_name_url as string) || "",
        limit_check: configuration?.tenantConfiguration?.limit_check || {},
        componentFeatures: (configuration?.tenantConfiguration?.component_feature as ComponentFeatures) || {},
        bbtiConfiguration: configuration?.bbtiConfiguration || {},
        leaseConfiguration: configuration?.leaseConfiguration || {},
        validationConfiguration: configuration?.validationConfiguration || {},
        insuranceConfiguration: configuration?.insuranceConfiguration || {},
        ntfConfiguration: configuration?.ntfConfiguration || {},
        downloadAppPageConfiguration: configuration?.downloadAppPageConfiguration || {},
        identityVerificationConfiguration: configuration?.identityVerificationConfiguration || {},
        sdkConfiguration: configuration?.sdkConfiguration || {},
    };

    const setFormValues = (updater: typeof formValues | ((prev: typeof formValues) => typeof formValues)) => {
        if (typeof updater === "function") {
            setConfiguration((prevConfig) => {
                const currentFormValues = {
                    brand: (prevConfig?.tenantConfiguration?.brand as Brand) || formValues.brand,
                    features: (prevConfig?.tenantConfiguration?.features as string[]) || formValues.features,
                    platform: (prevConfig?.tenantConfiguration?.platform as string) || formValues.platform,
                    selectedApp: (prevConfig?.tenantConfiguration?.selectedApp as string) || formValues.selectedApp,
                    marketing_name_url:
                        (prevConfig?.tenantConfiguration?.marketing_name_url as string) ||
                        formValues.marketing_name_url,
                    limit_check: prevConfig?.tenantConfiguration?.limit_check || formValues.limit_check,
                    componentFeatures:
                        (prevConfig?.tenantConfiguration?.component_feature as ComponentFeatures) ||
                        formValues.componentFeatures,
                    bbtiConfiguration: prevConfig?.bbtiConfiguration || formValues.bbtiConfiguration,
                    leaseConfiguration: prevConfig?.leaseConfiguration || formValues.leaseConfiguration,
                    validationConfiguration: prevConfig?.validationConfiguration || formValues.validationConfiguration,
                    insuranceConfiguration: prevConfig?.insuranceConfiguration || formValues.insuranceConfiguration,
                    ntfConfiguration: prevConfig?.ntfConfiguration || formValues.ntfConfiguration,
                    downloadAppPageConfiguration:
                        prevConfig?.downloadAppPageConfiguration || formValues.downloadAppPageConfiguration,
                    identityVerificationConfiguration:
                        prevConfig?.identityVerificationConfiguration || formValues.identityVerificationConfiguration,
                    sdkConfiguration: prevConfig?.sdkConfiguration || formValues.sdkConfiguration,
                };
                const updatedFormValues = updater(currentFormValues);
                return {
                    ...prevConfig,
                    tenantConfiguration: {
                        ...prevConfig?.tenantConfiguration,
                        brand: updatedFormValues.brand,
                        features: updatedFormValues.features,
                        platform: updatedFormValues.platform,
                        selectedApp: updatedFormValues.selectedApp,
                        marketing_name_url: updatedFormValues.marketing_name_url,
                        limit_check: updatedFormValues.limit_check,
                        component_feature: updatedFormValues.componentFeatures,
                    },
                    bbtiConfiguration: updatedFormValues.bbtiConfiguration,
                    leaseConfiguration: updatedFormValues.leaseConfiguration,
                    validationConfiguration: updatedFormValues.validationConfiguration,
                    insuranceConfiguration: updatedFormValues.insuranceConfiguration,
                    ntfConfiguration: updatedFormValues.ntfConfiguration,
                    downloadAppPageConfiguration: updatedFormValues.downloadAppPageConfiguration,
                    identityVerificationConfiguration: updatedFormValues.identityVerificationConfiguration,
                    sdkConfiguration: updatedFormValues.sdkConfiguration,
                } as Configuration;
            });
        } else {
            setConfiguration(
                (prevConfig) =>
                    ({
                        ...prevConfig,
                        tenantConfiguration: {
                            ...prevConfig?.tenantConfiguration,
                            brand: updater.brand,
                            features: updater.features,
                            platform: updater.platform,
                            selectedApp: updater.selectedApp,
                            marketing_name_url: updater.marketing_name_url,
                            limit_check: updater.limit_check,
                            component_feature: updater.componentFeatures,
                        },
                        bbtiConfiguration: updater.bbtiConfiguration,
                        leaseConfiguration: updater.leaseConfiguration,
                        validationConfiguration: updater.validationConfiguration,
                        insuranceConfiguration: updater.insuranceConfiguration,
                        ntfConfiguration: updater.ntfConfiguration,
                        downloadAppPageConfiguration: updater.downloadAppPageConfiguration,
                        identityVerificationConfiguration: updater.identityVerificationConfiguration,
                        sdkConfiguration: updater.sdkConfiguration,
                    }) as Configuration
            );
        }
    };

    const createTab = (localizationKey: string, feature: string): JSX.Element[] => {
        return [
            <Tab
                key={feature}
                disabled={isFeatureDisabled(feature)}
                className={isFeatureDisabled(feature) ? style.disabledTab : ""}
            >
                {localizationKey}
            </Tab>,
        ];
    };
    const listOfFeaturesToRemoveFromIntroCards = ["gathering_imei", "infogathering"];

    const handleSubmit = () => {
        function generateFeatures() {
            const enabledFeatures = [];

            for (const [featureKey, featureValue] of Object.entries(
                formValues.componentFeatures as ComponentFeatures
            )) {
                if (featureValue.enabled) {
                    enabledFeatures.push(featureKey);
                }
            }
            return enabledFeatures;
        }

        function generateComponentFeatures() {
            const enabledComponentsJSON: Record<string, string[]> = {};

            for (const [featureKey, featureValue] of Object.entries(
                formValues.componentFeatures as ComponentFeatures
            )) {
                if (featureValue.enabled) {
                    enabledComponentsJSON[featureKey] = featureValue.components
                        .filter((component: { enabled: boolean; name: string }) => component.enabled)
                        .map((component: { name: string }) => component.name);
                } else {
                    enabledComponentsJSON[featureKey] = [];
                }
            }
            return enabledComponentsJSON;
        }

        function generateIntroCards() {
            return generateFeatures().filter((item) => !listOfFeaturesToRemoveFromIntroCards.includes(item));
        }

        props.onSuccess({
            ...configuration,
            tenantConfiguration: {
                ...configuration.tenantConfiguration,
                brand: formValues.brand,
                platform: formValues.platform,
                selectedApp: formValues.selectedApp,
                marketing_name_url: formValues.marketing_name_url,
                limit_check: {
                    ...formValues.limit_check,
                },
                features: generateFeatures(),
                intro_cards: generateIntroCards(),
                component_features: generateComponentFeatures(),
            },
            bbtiConfiguration: {
                ...configuration.bbtiConfiguration,
                imeiMandatory: formValues.bbtiConfiguration.imeiMandatory,
                serialMandatory: formValues.bbtiConfiguration.serialMandatory,
                defaultProvider: formValues.bbtiConfiguration.defaultProvider,
                customer_punch_out: formValues.bbtiConfiguration.customer_punch_out,
                providers: formValues.bbtiConfiguration.providers,
                tests: formValues.bbtiConfiguration.tests.filter((test) => test.enabled),
            },
            leaseConfiguration: {
                ...configuration.leaseConfiguration,
                imeiMandatory: formValues.leaseConfiguration.imeiMandatory,
                serialMandatory: formValues.leaseConfiguration.serialMandatory,
                punchOutUrl: formValues.leaseConfiguration.punchOutUrl,
                customer_punch_out: formValues.leaseConfiguration.customer_punch_out,
                provider: formValues.leaseConfiguration.provider,
                apiConfig: formValues.leaseConfiguration.apiConfig,
                tests: formValues.leaseConfiguration.tests?.filter((test) => test.enabled),
            },
            validationConfiguration: {
                ...configuration.validationConfiguration,
                punchOutUrl: formValues.validationConfiguration.punchOutUrl,
                customer_punch_out: formValues.validationConfiguration.customer_punch_out,
                imeiMandatory: formValues.validationConfiguration.imeiMandatory,
                serialMandatory: formValues.validationConfiguration.serialMandatory,
                provider: formValues.validationConfiguration.provider,
                apiConfig: formValues.validationConfiguration.apiConfig,
                tests: formValues.validationConfiguration.tests.filter((test) => test.enabled),
            },
            insuranceConfiguration: {
                ...configuration.insuranceConfiguration,
                imeiMandatory: formValues.insuranceConfiguration.imeiMandatory,
                serialMandatory: formValues.insuranceConfiguration.serialMandatory,
                pricing: formValues.insuranceConfiguration.pricing,
                provider: formValues.insuranceConfiguration.provider,
                tests: formValues.insuranceConfiguration.tests.filter((test) => test.enabled),
            },
            ntfConfiguration: {
                ...configuration.ntfConfiguration,
                imeiMandatory: formValues.ntfConfiguration.imeiMandatory,
                serialMandatory: formValues.ntfConfiguration.serialMandatory,
                providers: formValues.ntfConfiguration.providers,
                thresholds: formValues.ntfConfiguration.thresholds,
                fault_codes: formValues.ntfConfiguration.fault_codes,
                tests: formValues.ntfConfiguration.tests.filter((test) => test.enabled),
            },
            downloadAppPageConfiguration: {
                ...configuration.downloadAppPageConfiguration,
                appIcon: formValues.downloadAppPageConfiguration.appIcon,
                headerLogo: formValues.downloadAppPageConfiguration.headerLogo,
                title: formValues.downloadAppPageConfiguration.title,
                androidFirebaseLink: formValues.downloadAppPageConfiguration.androidFirebaseLink,
                huaweiFirebase: formValues.downloadAppPageConfiguration.huaweiFirebase,
                iOSFirebaseLink: formValues.downloadAppPageConfiguration.iOSFirebaseLink,
                licenseFileType: formValues.downloadAppPageConfiguration.licenseFileType,
                autoForward: formValues.downloadAppPageConfiguration.autoForward,
            },
            identityVerificationConfiguration: {
                ...configuration.identityVerificationConfiguration,
                endpoint: formValues.identityVerificationConfiguration.endpoint,
                bodyFieldName: formValues.identityVerificationConfiguration.bodyFieldName,
                apiConfiguration: formValues.identityVerificationConfiguration.apiConfiguration,
                oAuthConfiguration: formValues.identityVerificationConfiguration.oAuthConfiguration,
            },
            sdkConfiguration: {
                ...configuration.sdkConfiguration,
                tests: formValues.sdkConfiguration.tests?.filter((test) => test.enabled),
            },
        });
    };

    function isFeatureDisabled(feature: string) {
        return (
            Object.keys(formValues.componentFeatures).length === 0 ||
            (formValues.platform.toLowerCase() === "sdk" && feature !== "sdk") ||
            (feature === "sdk" && formValues.platform.toLowerCase() !== "sdk") || // new condition
            (formValues.componentFeatures[feature] && !formValues.componentFeatures[feature].enabled)
        );
    }

    return (
        <>
            {props.configuration === undefined ? (
                ""
            ) : (
                <>
                    {props.runWizard ? (
                        <Wizard
                            configuration={configuration}
                            setFormValues={setConfiguration}
                            onSave={handleSubmit}
                            allowTestSelection={true}
                            allowAdvancedSettings={true}
                        />
                    ) : (
                        <div className={style.tabContainer}>
                            <div>
                                <Checkbox
                                    labelKey={t("Configuration.showAdvancedSettings")}
                                    checkboxId="showAdvanced"
                                    checked={showAdvanced}
                                    onChange={setShowAdvanced}
                                />
                            </div>
                            <div
                                data-testid={
                                    testIds.workArea.license.configure.manageConfigurationDialog.configInput.itself
                                }
                            >
                                <Tabs>
                                    <TabList>
                                        <Tab>{t("Configuration.viewConfigureDialog.genericTab")}</Tab>
                                        {createTab(t("Configuration.viewConfigureDialog.buyBackTradeInTab"), "TradeIn")}
                                        {createTab(t("Configuration.viewConfigureDialog.leaseTab"), "Lease")}
                                        {createTab(t("Configuration.viewConfigureDialog.validationTab"), "Validation")}
                                        {createTab(
                                            t("Configuration.viewConfigureDialog.insuranceTab"),
                                            "PreSalesInsurance"
                                        )}
                                        {createTab(t("Configuration.viewConfigureDialog.ntfTab"), "FaultCheck")}
                                        {createTab(
                                            t("Configuration.viewConfigureDialog.identifierVerificationTab"),
                                            "infogathering"
                                        )}
                                        {createTab(sdkTabName, "sdk")}
                                        <Tab>{t("Configuration.viewConfigureDialog.downloadServerTab")}</Tab>
                                    </TabList>

                                    <div className={style.tabPanel}>
                                        <TabPanel>
                                            <GenericConfigurationForm
                                                formValues={formValues}
                                                setFormValues={setFormValues}
                                                featuresWithAvailableLicenses={props.featuresWithAvailableLicenses}
                                                showAdvanced={showAdvanced}
                                            />
                                        </TabPanel>
                                        <TabPanel>
                                            <BbtiConfigurationForm
                                                formValues={formValues}
                                                setFormValues={setFormValues}
                                                showAdvanced={showAdvanced}
                                            />
                                        </TabPanel>
                                        <TabPanel>
                                            <LeaseConfigurationForm
                                                formValues={formValues}
                                                setFormValues={setFormValues}
                                                showAdvanced={showAdvanced}
                                            />
                                        </TabPanel>
                                        <TabPanel>
                                            <ValidationConfigurationForm
                                                formValues={formValues}
                                                setFormValues={setFormValues}
                                                showAdvanced={showAdvanced}
                                            />
                                        </TabPanel>
                                        <TabPanel>
                                            <InsuranceConfigurationForm
                                                formValues={formValues}
                                                setFormValues={setFormValues}
                                                showAdvanced={showAdvanced}
                                            />
                                        </TabPanel>
                                        <TabPanel>
                                            <NtfConfigurationForm
                                                formValues={formValues}
                                                setFormValues={setFormValues}
                                                showAdvanced={showAdvanced}
                                            />
                                        </TabPanel>
                                        <TabPanel>
                                            <IdentityVerificationConfigurationForm
                                                formValues={formValues}
                                                setFormValues={setFormValues}
                                                showAdvanced={showAdvanced}
                                            />
                                        </TabPanel>
                                        <TabPanel>
                                            <SdkConfigurationForm
                                                formValues={formValues}
                                                setFormValues={setFormValues}
                                                showAdvanced={showAdvanced}
                                            />
                                        </TabPanel>
                                        <TabPanel>
                                            <DownloadServerConfigurationForm
                                                formValues={formValues}
                                                setFormValues={setFormValues}
                                                showAdvanced={showAdvanced}
                                            />
                                        </TabPanel>
                                    </div>
                                </Tabs>
                            </div>
                            <div className={form.buttonContainer}>
                                <button
                                    className={buttons.primaryButtonWithoutIcon}
                                    onClick={handleSubmit}
                                    data-testid={
                                        testIds.workArea.license.configure.manageConfigurationDialog.saveButton
                                    }
                                >
                                    {t("Configuration.editConfigureDialog.saveButton")}
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </>
    );
}
